import React, { useEffect, useState } from 'react';
import { X, Download, ExternalLink, Loader } from 'lucide-react';

/**
 * Visionneuse de justificatif (photo ou PDF) affichée dans la page, plutôt
 * que dans un nouvel onglet : sur tablette/mobile, window.open après un
 * chargement asynchrone est souvent bloqué par le navigateur.
 *
 * `attachment` : { id, fileName, contentType } ; `loadUrl(id)` renvoie une
 * URL blob locale (voir downloadAttachment dans utils/api.js).
 */
const AttachmentViewer = ({ attachment, loadUrl, onClose }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setUrl(null);
    setError(false);
    loadUrl(attachment.id)
      .then((u) => {
        objectUrl = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, loadUrl]);

  // Échap ferme la visionneuse.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isImage = (attachment.contentType || '').startsWith('image/');
  const isPdf = attachment.contentType === 'application/pdf';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <span className="font-medium text-gray-800 truncate" title={attachment.fileName}>
            {attachment.fileName}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {url && (
              <>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded hover:bg-gray-100 text-gray-600"
                  title="Ouvrir dans un nouvel onglet"
                >
                  <ExternalLink size={18} />
                </a>
                <a
                  href={url}
                  download={attachment.fileName}
                  className="p-2 rounded hover:bg-gray-100 text-gray-600"
                  title="Télécharger"
                >
                  <Download size={18} />
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded hover:bg-gray-100 text-gray-600"
              title="Fermer"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
          {error ? (
            <p className="text-red-600 text-sm px-4 text-center">Impossible de charger le justificatif.</p>
          ) : !url ? (
            <Loader size={28} className="animate-spin text-gray-500" />
          ) : isImage ? (
            <img src={url} alt={attachment.fileName} className="max-w-full max-h-full object-contain" />
          ) : isPdf ? (
            <iframe src={url} title={attachment.fileName} className="w-full h-full border-0" />
          ) : (
            <p className="text-gray-600 text-sm px-4 text-center">
              Aperçu indisponible : utilise « Ouvrir » ou « Télécharger ».
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentViewer;
